import { useTranslation } from "react-i18next";
import { i18nMessage } from "@/lib/i18nMessage";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import StyledPhoneInput from "../styled/StyledPhoneInput";
import { OpeningHours, PickUpLocationFormData } from "@/types/forms/auth/registerFormData.form";
import { DaysPicker } from "./DaysPicker";
import { DayTimeSlots } from "./DayTimeSlots";
import { ControlledUnifiedInput } from "../ui/customs/controlled-unified-input";

type PickUpLocationFormProps = {
  control: Control<PickUpLocationFormData>;
  watch: UseFormWatch<PickUpLocationFormData>;
  setValue: UseFormSetValue<PickUpLocationFormData>;
  errors: FieldErrors<PickUpLocationFormData>;
  showIsDefault?: boolean;
  register: UseFormRegister<PickUpLocationFormData>;
};

const DEFAULT_SLOT = { open: "08:00", close: "18:00" };

export const PickUpLocationForm = ({
  control,
  watch,
  setValue,
  errors,
  showIsDefault = true,
  register,
}: PickUpLocationFormProps) => {
  const { t } = useTranslation("boClients");
  const translateMessage = useTranslateMessage();
  const openingHours: OpeningHours = watch("openingHours") ?? {};

  const selectedDays = Object.entries(openingHours)
    .filter(([, slots]) => slots.length > 0)
    .map(([day]) => parseInt(day, 10));

  const toggleDay = (dayIndex: number) => {
    const dayKey = dayIndex.toString();
    const currentSlots = openingHours[dayKey] ?? [];

    if (currentSlots.length > 0) {
      setValue("openingHours", {
        ...openingHours,
        [dayKey]: [],
      });
    } else {
      setValue("openingHours", {
        ...openingHours,
        [dayKey]: [{ ...DEFAULT_SLOT }],
      });
    }
  };

  const addSlot = (dayIndex: number) => {
    const dayKey = dayIndex.toString();
    const currentSlots = openingHours[dayKey] ?? [];
    setValue("openingHours", {
      ...openingHours,
      [dayKey]: [...currentSlots, { open: "", close: "" }],
    });
  };

  const removeSlot = (dayIndex: number, slotIndex: number) => {
    const dayKey = dayIndex.toString();
    const currentSlots = openingHours[dayKey] ?? [];
    const newSlots = currentSlots.filter((_, i) => i !== slotIndex);
    setValue("openingHours", {
      ...openingHours,
      [dayKey]: newSlots,
    });
  };

  const updateSlot = (
    dayIndex: number,
    slotIndex: number,
    field: "open" | "close",
    value: string,
  ) => {
    const dayKey = dayIndex.toString();
    const currentSlots = openingHours[dayKey] ?? [];
    const newSlots = currentSlots.map((slot, i) =>
      i === slotIndex ? { ...slot, [field]: value } : slot,
    );
    setValue("openingHours", {
      ...openingHours,
      [dayKey]: newSlots,
    });
  };

  const formErrors = (
    errors as FieldErrors<PickUpLocationFormData> & { ""?: { message?: string } }
  )[""];

  const openingHoursError = errors.openingHours as { message?: string } | undefined;

  return (
    <>
      <ControlledUnifiedInput
        name="name"
        label={t("pickUpLocationForm.nameLabel")}
        control={control}
      />
      <ControlledUnifiedInput
        name="street"
        label={t("pickUpLocationForm.streetLabel")}
        control={control}
      />
      <div className="flex flex-col gap-2 md:flex-row">
        <ControlledUnifiedInput
          name="postalCode"
          className="w-full md:w-[30%]"
          label={t("pickUpLocationForm.postalCodeLabel")}
          control={control}
        />
        <ControlledUnifiedInput
          name="city"
          label={t("pickUpLocationForm.cityLabel")}
          className="w-full md:w-[70%]"
          control={control}
        />
      </div>
      <StyledPhoneInput
        name="contactNumber"
        title={t("pickUpLocationForm.contactNumberLabel")}
        control={control}
      />
      <StyledPhoneInput
        name="contactNumber2"
        title={t("pickUpLocationForm.contactNumber2Label")}
        control={control}
      />

      <div className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-blue-600">
          {t("pickUpLocationForm.openingHoursTitle")}
        </h2>

        <DaysPicker selectedDays={selectedDays} onToggleDay={toggleDay} />

        <DayTimeSlots
          openingHours={openingHours}
          selectedDays={selectedDays}
          onAddSlot={addSlot}
          onRemoveSlot={removeSlot}
          onUpdateSlot={updateSlot}
        />

        {(formErrors?.message === i18nMessage("shared:forms.register.openingHoursInconsistent") ||
          openingHoursError?.message) && (
          <p className="text-xs text-red-500">
            {translateMessage(formErrors?.message || openingHoursError?.message)}
          </p>
        )}
      </div>

      <div className={showIsDefault ? "flex flex-row gap-2 pt-4" : "hidden"}>
        <input type="checkbox" className="peer w-[16px]" {...register("isDefault")} />
        {showIsDefault && (
          <label className="text-sm font-medium text-gray-600">
            {t("pickUpLocationForm.isDefaultLabel")}
          </label>
        )}
      </div>
    </>
  );
};
